import Constants from 'expo-constants';
import { Platform } from 'react-native';

import { isMobile } from '@/utils/getPlatform';
import { isWidget } from '@/utils/isWidget';
import type {
  StandbyApp,
  StandbyPlatform,
  StandbySurface,
  StandbyUserType,
} from '@/utils/tracking/standbyAnalyticsCore';

// Single source of truth for who this client is. Every destination — API
// headers, RudderStack, Clarity, Datadog RUM, Sentry — derives its field names
// and values from here; nothing else in the app may spell out a client key.
// Canonical vocabulary: client.app, client.version, client.platform,
// client.surface. Encodings: header `X-Client-<Key>`, tracking `client_<key>`,
// and squire-api logs them to Datadog as dotted `client.<key>`. SQR-14516.

// `satisfies` ties this to the shared standby core, so the app id can't drift
// from the cross-app enum (SQR-16001).
export const CLIENT_APP = 'squire-mobile-flagship' satisfies StandbyApp;

// SQR-16001: the standby wire schema's enums (standbyAnalyticsCore, byte-identical
// with squire-mobile-commander) are the source of truth. These guards fail to
// compile if a client platform/surface is added below without also adding it to
// the shared core — so the next surface can't ship outside the standby enum.
type AssertAssignable<Sub extends Sup, Sup> = Sub;
type _ClientPlatformInCore = AssertAssignable<ClientPlatform, StandbyPlatform>;
type _ClientSurfaceInCore = AssertAssignable<ClientSurface, StandbySurface>;

export type ClientPlatform = 'ios' | 'android' | 'web';
export type ClientAppVariant = 'default' | 'branded' | 'flex';
export type WebFormFactor = 'mobile' | 'desktop' | 'widget';
export type ClientSurface =
  | 'ios'
  | 'android'
  | 'mobile_web'
  | 'desktop_web'
  | 'widget'
  | 'branded_ios'
  | 'branded_android'
  | 'flex_ios'
  | 'flex_android';

export interface ClientIdentity {
  app: string;
  version: string | undefined;
  platform: ClientPlatform;
  surface: ClientSurface;
}

export type ClientIdentityKey = keyof ClientIdentity;

export const CLIENT_IDENTITY_KEYS = [
  'app',
  'version',
  'platform',
  'surface',
] as const satisfies readonly ClientIdentityKey[];

const CLIENT_PLATFORMS: readonly ClientPlatform[] = ['ios', 'android', 'web'];
const APP_VARIANTS: readonly ClientAppVariant[] = [
  'default',
  'branded',
  'flex',
];

// Adding a surface: add the value to ClientSurface and a row here. Branded and
// flex builds set `extra.APP_VARIANT` in app.config; trackers never change.
const NATIVE_SURFACES: Record<
  ClientAppVariant,
  Record<Exclude<ClientPlatform, 'web'>, ClientSurface>
> = {
  default: { ios: 'ios', android: 'android' },
  branded: { ios: 'branded_ios', android: 'branded_android' },
  flex: { ios: 'flex_ios', android: 'flex_android' },
};

const WEB_SURFACES: Record<WebFormFactor, ClientSurface> = {
  mobile: 'mobile_web',
  desktop: 'desktop_web',
  widget: 'widget',
};

export interface ClientSurfaceInput {
  platform: ClientPlatform;
  variant: ClientAppVariant;
  webFormFactor: WebFormFactor;
}

export function resolveClientSurface({
  platform,
  variant,
  webFormFactor,
}: ClientSurfaceInput): ClientSurface {
  if (platform === 'web') {
    return WEB_SURFACES[webFormFactor];
  }
  return NATIVE_SURFACES[variant][platform];
}

const toClientPlatform = (os: string): ClientPlatform =>
  (CLIENT_PLATFORMS as readonly string[]).includes(os)
    ? (os as ClientPlatform)
    : 'web';

const toAppVariant = (value: unknown): ClientAppVariant =>
  typeof value === 'string' &&
  (APP_VARIANTS as readonly string[]).includes(value)
    ? (value as ClientAppVariant)
    : 'default';

export interface ClientIdentityDeps {
  os: string;
  version: unknown;
  variant: unknown;
  isMobileViewport: () => boolean;
  isWidget: boolean;
}

const resolveWebFormFactor = (deps: ClientIdentityDeps): WebFormFactor => {
  if (deps.isWidget) return 'widget';
  return deps.isMobileViewport() ? 'mobile' : 'desktop';
};

export function computeClientIdentity(
  deps: ClientIdentityDeps,
): ClientIdentity {
  const platform = toClientPlatform(deps.os);
  const variant = toAppVariant(deps.variant);
  const webFormFactor: WebFormFactor =
    platform === 'web' ? resolveWebFormFactor(deps) : 'mobile';

  return {
    app: CLIENT_APP,
    version: typeof deps.version === 'string' ? deps.version : undefined,
    platform,
    surface: resolveClientSurface({ platform, variant, webFormFactor }),
  };
}

// Boot-time snapshot: API headers are fixed when the client is constructed and
// analytics must agree with API logs, so a window resized across the tablet
// breakpoint mid-session keeps its original surface.
let cachedIdentity: ClientIdentity | undefined;

export function getClientIdentity(): ClientIdentity {
  cachedIdentity ??= computeClientIdentity({
    os: Platform.OS,
    version: Constants.expoConfig?.extra?.EXPO_APP_VERSION,
    variant: Constants.expoConfig?.extra?.APP_VARIANT,
    isMobileViewport: isMobile,
    isWidget,
  });
  return cachedIdentity;
}

const toHeaderName = (key: ClientIdentityKey) =>
  `X-Client-${key.charAt(0).toUpperCase()}${key.slice(1)}`;

const toTrackingPropertyName = (key: ClientIdentityKey) => `client_${key}`;

// SQR-16001: standby events carry a unified schema whose keys drop the `client_`
// prefix, and `version` becomes `app_version` to disambiguate it from any future
// event-level version. This vocabulary lives here so it stays the single source
// of truth alongside the header and legacy tracking encodings.
const STANDBY_EVENT_PREFIX = 'standby_';

const toStandbyTrackingPropertyName = (key: ClientIdentityKey) =>
  key === 'version' ? 'app_version' : key;

function encode(
  identity: ClientIdentity,
  toName: (key: ClientIdentityKey) => string,
): Record<string, string> {
  const encoded: Record<string, string> = {};
  for (const key of CLIENT_IDENTITY_KEYS) {
    const value = identity[key];
    if (value !== undefined) {
      encoded[toName(key)] = value;
    }
  }
  return encoded;
}

/** `X-Client-App`, `X-Client-Version`, `X-Client-Platform`, `X-Client-Surface`. */
export function getClientHeaders(
  identity: ClientIdentity = getClientIdentity(),
): Record<string, string> {
  return encode(identity, toHeaderName);
}

/** `client_app`, `client_version`, `client_platform`, `client_surface`. */
export function getClientTrackingProperties(
  identity: ClientIdentity = getClientIdentity(),
): Record<string, string> {
  return encode(identity, toTrackingPropertyName);
}

/** Unified standby schema: `app`, `app_version`, `platform`, `surface`. */
export function getStandbyTrackingProperties(
  identity: ClientIdentity = getClientIdentity(),
): Record<string, string> {
  return encode(identity, toStandbyTrackingPropertyName);
}

/**
 * Client schema for an event's properties, selected by event name. Standby
 * events (SQR-16001) emit the unified schema plus `user_type`; every other event
 * keeps the legacy `client_*` keys byte-for-byte unchanged and carries no
 * `user_type`. Flagship is the customer app, so every standby actor is a
 * customer — the value is constant here rather than resolved per call site.
 */
export function getTrackingPropertiesForEvent(
  eventName: string,
  identity: ClientIdentity = getClientIdentity(),
): Record<string, string> {
  return eventName.startsWith(STANDBY_EVENT_PREFIX)
    ? {
        ...getStandbyTrackingProperties(identity),
        user_type: 'customer' satisfies StandbyUserType,
      }
    : getClientTrackingProperties(identity);
}
